'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  RefreshCw,
  Shield,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { ApiError } from '@/lib/api';
import {
  forgotPassword,
  resendOtp,
  resetPassword,
  verifyOtp,
} from '@/lib/auth-api';

type Step = 'email' | 'otp' | 'password' | 'success';

interface EmailForm {
  email: string;
}

interface PasswordForm {
  password: string;
  confirmPassword: string;
}

const OTP_LENGTH = 6;
const OTP_TTL_SECONDS = 180;
const MAX_SENDS = 5;

export default function ForgotPasswordClient() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState('');
  const [otpLocked, setOtpLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(OTP_TTL_SECONDS);
  const [remainingSends, setRemainingSends] = useState(MAX_SENDS - 1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const emailForm = useForm<EmailForm>({
    defaultValues: { email: '' },
  });

  const passwordForm = useForm<PasswordForm>({
    defaultValues: { password: '', confirmPassword: '' },
  });

  useEffect(() => {
    if (step !== 'otp' && step !== 'password') return;
    if (otpExpiresIn <= 0) return;
    const timer = setTimeout(() => setOtpExpiresIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, otpExpiresIn]);

  const applyOtpWindow = (expiresIn?: number, remaining?: number) => {
    setOtpExpiresIn(expiresIn || OTP_TTL_SECONDS);
    if (typeof remaining === 'number') {
      setRemainingSends(remaining);
    }
  };

  const applyOtpFailure = (
    error: unknown,
    fallback: string,
    { alwaysToast = false }: { alwaysToast?: boolean } = {}
  ) => {
    const message = error instanceof ApiError ? error.message : fallback;
    const locked =
      error instanceof ApiError &&
      (error.status === 429 ||
        /too many invalid otp attempts/i.test(message));
    setOtpError(message);
    setOtpLocked(locked);
    if (alwaysToast || !locked) {
      toast.error(message);
    }
  };

  const onEmailSubmit = async (data: EmailForm) => {
    setLoading(true);
    try {
      const result = await forgotPassword(data.email.trim());
      setEmail(data.email.trim().toLowerCase());
      setOtp(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      setOtpLocked(false);
      applyOtpWindow(
        result.data?.expires_in_seconds,
        result.data?.remaining_sends
      );
      setStep('otp');
      toast.success(result.message || 'Password reset OTP has been sent');
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Failed to send OTP. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value) || otpLocked) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setOtpError('');
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);
    const next = [...otp];
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async () => {
    if (otpLocked) return;
    const code = otp.join('');
    if (code.length < OTP_LENGTH) {
      setOtpError(`Enter all ${OTP_LENGTH} digits of your OTP`);
      return;
    }
    if (otpExpiresIn <= 0) {
      setOtpError('OTP has expired. Please resend a new one.');
      return;
    }

    setLoading(true);
    try {
      await verifyOtp(email, code);
      setOtpLocked(false);
      setStep('password');
      toast.success('OTP verified. Set your new password.');
    } catch (error) {
      applyOtpFailure(error, 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (remainingSends <= 0 && otpExpiresIn > 0 && !otpLocked) {
      toast.error(
        `Too many OTP requests. Please wait ${otpExpiresIn}s before trying again.`
      );
      return;
    }

    setLoading(true);
    try {
      const result = await resendOtp(email);
      setOtp(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      setOtpLocked(false);
      applyOtpWindow(
        result.data?.expires_in_seconds,
        result.data?.remaining_sends
      );
      toast.success(result.message || 'A new OTP has been sent');
      inputRefs.current[0]?.focus();
    } catch (error) {
      applyOtpFailure(error, 'Failed to resend OTP. Please try again.', {
        alwaysToast: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    const code = otp.join('');
    setLoading(true);
    try {
      const result = await resetPassword(email, code, data.password);
      setStep('success');
      toast.success(result.message || 'Password reset successful');
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Failed to reset password. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 bg-card border-b border-border flex items-center px-6">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <div>
            <span className="font-700 text-sm text-foreground block leading-tight">
              Birla Open Minds
            </span>
            <span className="text-xs text-muted-foreground">
              International School
            </span>
          </div>
        </div>
        <div className="flex-1" />
        <Link
          href="/"
          className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
        >
          Back to Login
          <ChevronRight size={13} />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {step === 'email' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Mail size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-base font-700 text-foreground">
                    Forgot Password
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Step 1 of 3 — Enter your account email
                  </p>
                </div>
              </div>

              <form
                onSubmit={emailForm.handleSubmit(onEmailSubmit)}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="flipbook.admin@yopmail.com"
                    className={`w-full px-4 py-2.5 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                      emailForm.formState.errors.email
                        ? 'border-red-400'
                        : 'border-border'
                    }`}
                    {...emailForm.register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Enter a valid email address',
                      },
                    })}
                  />
                  {emailForm.formState.errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {emailForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                  <Shield
                    size={14}
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs text-amber-700">
                    A 6-digit OTP will be sent to your email. It expires in 3
                    minutes. You can resend up to 5 times within that window.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Send OTP
                      <ChevronRight size={15} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {step === 'otp' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Lock size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-base font-700 text-foreground">
                    Enter OTP
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Step 2 of 3 — Check your inbox
                  </p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  OTP sent to{' '}
                  <span className="font-600 text-foreground">{email}</span>
                </p>
                {!otpError && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Expires in{' '}
                    <span className="font-600 text-foreground">
                      {formatTime(Math.max(otpExpiresIn, 0))}
                    </span>
                  </p>
                )}
              </div>

              <div
                className="flex items-center justify-center gap-2 mb-4"
                onPaste={otpLocked ? undefined : handleOTPPaste}
              >
                {otp.map((digit, i) => (
                  <input
                    key={`forgot-otp-${i}`}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    disabled={otpLocked || loading}
                    onChange={(e) => handleOTPChange(i, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(i, e)}
                    className={`otp-input-box ${digit ? 'filled' : ''} ${
                      otpError ? 'border-red-400' : ''
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                    aria-label={`OTP digit ${i + 1}`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="text-red-500 text-xs text-center mb-3">{otpError}</p>
              )}

              <button
                onClick={handleVerifyOtp}
                disabled={
                  loading ||
                  otpLocked ||
                  otp.join('').length < OTP_LENGTH
                }
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed mb-3"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle size={15} />
                    Verify OTP
                  </>
                )}
              </button>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setStep('email');
                    setOtp(Array(OTP_LENGTH).fill(''));
                    setOtpError('');
                    setOtpLocked(false);
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft size={12} />
                  Change email
                </button>
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="flex items-center gap-1 text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline transition-colors font-500"
                >
                  <RefreshCw size={12} />
                  Resend OTP
                </button>
              </div>
            </div>
          )}

          {step === 'password' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Lock size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-base font-700 text-foreground">
                    Set New Password
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Step 3 of 3 — Choose a strong password
                  </p>
                </div>
              </div>

              <form
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      className={`w-full px-4 py-2.5 pr-10 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                        passwordForm.formState.errors.password
                          ? 'border-red-400'
                          : 'border-border'
                      }`}
                      {...passwordForm.register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                          message:
                            'Must include uppercase, lowercase, and a number',
                        },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordForm.formState.errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {passwordForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      className={`w-full px-4 py-2.5 pr-10 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                        passwordForm.formState.errors.confirmPassword
                          ? 'border-red-400'
                          : 'border-border'
                      }`}
                      {...passwordForm.register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) =>
                          value === passwordForm.getValues('password') ||
                          'Passwords do not match',
                      })}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword((value) => !value)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  {passwordForm.formState.errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">
                      {passwordForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ChevronRight size={15} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {step === 'success' && (
            <div className="bg-card border border-emerald-200 rounded-xl p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <h2 className="text-base font-700 text-foreground mb-1">
                Password Updated
              </h2>
              <p className="text-xs text-muted-foreground mb-6">
                You can now sign in with your new password.
              </p>
              <Link
                href="/"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                Go to Sign In
                <ChevronRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
